import React, { useEffect, useState, useRef } from "react";
import { fetchApi } from "../../apis/Index";
import { toast } from "react-hot-toast";
import AddNailsModal from "./AddNailsModal";
import AddAccessoryModal from "./AddAccessoryModal";

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [filter, setFilter] = useState("all");
  
  // Modal states
  const [isNailsModalOpen, setIsNailsModalOpen] = useState(false);
  const [isAccessoryModalOpen, setIsAccessoryModalOpen] = useState(false);
  
  const menuRef = useRef(null);

  useEffect(() => {
    fetchProducts();
    
    // Close dropdown on outside click
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowAddMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchProducts = async () => {
    try {
      const result = await fetchApi("https://wittynailtip.com/backend/product.php");
      if (result.data.status === 'success') {
        setProducts(result.data.data);
      } else {
        toast.error(result.data.message || "Failed to fetch products");
      }
    } catch (error) {
      toast.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = (type) => {
    setShowAddMenu(false);
    if (type === "Nails") {
      setIsNailsModalOpen(true);
    } else if (type === "Accessory") {
      setIsAccessoryModalOpen(true);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to completely delete this product from Stripe and the database?")) return;
    
    try {
      const response = await fetch("https://wittynailtip.com/backend/admin-delete-product.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ product_id: productId })
      });
      const result = await response.json();
      if (result.success) {
        toast.success("Product deleted successfully");
        fetchProducts(); // Refresh the list
      } else {
        toast.error(result.message || "Failed to delete product");
      }
    } catch (error) {
      toast.error("Error deleting product");
    }
  };

  const filteredProducts = products.filter((p) => {
    if (filter === "all") return true;
    const cat = p.category ? p.category.toLowerCase() : "";
    if (filter === "handmade") return cat.includes("handmade");
    if (filter === "mechanical") return cat.includes("machine") || cat.includes("mechanical");
    if (filter === "accessory") return cat.includes("accessory");
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FFB7CF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 font-jost">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 relative">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Products</h1>
          <p className="text-gray-500 mt-1">Manage all nails and accessories.</p>
        </div>
        
        <div className="flex items-center gap-3 w-full md:w-auto">
          <select 
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2.5 rounded-full border border-gray-200 text-gray-700 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#FFB7CF]/50"
          >
            <option value="all">All Items</option>
            <option value="handmade">Handmade Nails</option>
            <option value="mechanical">Mechanical Nails</option>
            <option value="accessory">Accessories</option>
          </select>

          <div className="relative" ref={menuRef}>
            <button 
              onClick={() => setShowAddMenu(!showAddMenu)}
              className="bg-[#FFB7CF] hover:bg-[#ff9cbb] text-white px-6 py-2.5 rounded-full font-semibold transition-colors shadow-sm flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
              Add Product
            </button>
            
            {showAddMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
                <button onClick={() => handleAddClick("Nails")} className="w-full text-left px-4 py-3 hover:bg-gray-50 text-gray-700 font-medium border-b border-gray-50 transition-colors">
                  + Nails
                </button>
                <button onClick={() => handleAddClick("Accessory")} className="w-full text-left px-4 py-3 hover:bg-gray-50 text-gray-700 font-medium transition-colors">
                  + Accessory
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-500">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50/50">
              <tr>
                <th scope="col" className="px-6 py-4 rounded-tl-3xl">Image</th>
                <th scope="col" className="px-6 py-4">Name</th>
                <th scope="col" className="px-6 py-4">Category</th>
                <th scope="col" className="px-6 py-4">Price</th>
                <th scope="col" className="px-6 py-4">Colors</th>
                <th scope="col" className="px-6 py-4 text-right rounded-tr-3xl">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No products found for this filter.
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product) => (
                  <tr key={product.id} className="bg-white border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="w-12 h-12 rounded-xl bg-gray-100 overflow-hidden">
                        <img 
                          src={product.images && product.images.length > 0 ? product.images[0] : "/logo.webp"} 
                          alt={product.name} 
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = "/logo.webp"; }}
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-900">
                      {product.name}
                    </td>
                    <td className="px-6 py-4 capitalize">
                      <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold">
                        {product.category || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-semibold text-gray-900">
                      ${product.prices && product.prices.length > 0 ? product.prices[0].unit_amount : "0.00"}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-1">
                        {product.colors && Object.keys(product.colors).length > 0 ? Object.values(product.colors).map((color, idx) => (
                          <div key={idx} className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: color }}></div>
                        )) : "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button className="text-gray-400 hover:text-[#FFB7CF] transition-colors p-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg>
                      </button>
                      <button onClick={() => handleDeleteProduct(product.id)} className="text-gray-400 hover:text-red-500 transition-colors p-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Modals */}
      <AddNailsModal 
        isOpen={isNailsModalOpen} 
        onClose={() => setIsNailsModalOpen(false)} 
        onSuccess={fetchProducts} 
      />
      <AddAccessoryModal 
        isOpen={isAccessoryModalOpen} 
        onClose={() => setIsAccessoryModalOpen(false)} 
        onSuccess={fetchProducts} 
      />
    </div>
  );
};

export default AdminProducts;
